import { useEffect, useState } from "react";
import { Settings2, ShieldAlert, XCircle, Loader2, Mail,
         FileText, Download, AlertTriangle, Info, Sparkles, FileStack,
         CheckCircle2, RefreshCw, FileCheck2, Clock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * CFDI SOFOM · Fase A + candados Producción (feb-2026).
 *  - Bandeja de CFDI emitidos (vigentes / cancelados / error).
 *  - Bandeja de pagos timbrables (aún sin CFDI vigente).
 *  - Wizard "Configuración de facturación": crear organización Facturapi + subir CSD + guardar Test Key.
 *  - Preview del CFDI antes de timbrar (desglose + validaciones).
 *  - Advertencias legales, badge dinámico por `modo_operacion`, banner permanente.
 *  - Diálogo de confirmación explícita antes de cada timbrado; en producción exige
 *    checkbox + teclear "TIMBRAR" (regla dura del PO — CFDI producción = SAT REAL,
 *    no existe timbrado "real pero de prueba").
 *  - Estado visible del candado super_admin `emision_autorizada`. Cuando está OFF,
 *    los botones de timbrado quedan deshabilitados por completo — el backend responde
 *    HTTP 403 con `codigo="emision_no_autorizada"` como defensa en profundidad.
 */

const REGIMENES_SAT = [
  { key: "601", label: "601 · General de Ley PM" },
  { key: "603", label: "603 · Personas Morales con Fines no Lucrativos" },
  { key: "605", label: "605 · Sueldos y Salarios" },
  { key: "606", label: "606 · Arrendamiento" },
  { key: "608", label: "608 · Demás ingresos (PF)" },
  { key: "612", label: "612 · Actividades Empresariales y Profesionales (PF)" },
  { key: "621", label: "621 · Incorporación Fiscal" },
  { key: "625", label: "625 · Plataformas Tecnológicas (PF)" },
  { key: "626", label: "626 · Régimen Simplificado de Confianza (RESICO)" },
];

// ============================================================================
// BannerManifiesto — chip permanente del estado de la Carta Manifiesto en la
// pantalla principal /sofom/cfdi cuando el módulo ya está configurado.
// Fail-safe: si el backend no puede confirmar, mostramos "no verificado" nunca
// "firmada" por omisión. No bloquea el timbrado; sólo informa (regla PO).
// ============================================================================
function BannerManifiesto() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sofom/cfdi/manifiesto/estado");
      setInfo(data);
    } catch (_e) {
      setInfo({ estado: "no_verificado",
                detalle: "No se pudo consultar el estado. Reintenta.",
                portal_firma_url: "https://www.facturapi.io/manifiesto" });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  if (loading && !info) return null;
  if (!info || info.estado === "sin_organizacion") return null;

  // "firmada" → chip sutil verde; NO ocupa banner grande porque ya está resuelto.
  if (info.estado === "firmada") {
    return (
      <div data-testid="cfdi-banner-manifiesto-firmada"
           className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
        <CheckCircle2 className="h-4 w-4" />
        <span><strong>Carta Manifiesto firmada.</strong> Facturapi no reporta pasos pendientes.</span>
        <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px]"
                onClick={cargar} data-testid="cfdi-banner-manifiesto-refrescar">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
          Verificar
        </Button>
      </div>
    );
  }

  // Pendiente o no verificado → aviso ámbar (no bloquea).
  const esPend = info.estado === "pendiente";
  return (
    <div data-testid={`cfdi-banner-manifiesto-${info.estado}`}
         className={`rounded-md border p-3 text-xs ${
           esPend
             ? "border-amber-300 bg-amber-50 text-amber-900"
             : "border-slate-300 bg-slate-50 text-slate-800"
         }`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold">
            {esPend
              ? "Carta Manifiesto pendiente de firma"
              : "Carta Manifiesto no verificada"}
          </p>
          <p className="mt-1">{info.detalle}</p>
          <p className="mt-1 opacity-90">
            Reglas RMF 2026 (2.7.2.5 y 2.7.2.7). El timbrado NO se bloquea, pero se
            recomienda firmar cuanto antes.
          </p>
          {info.rfc_entidad && (
            <p className="mt-1 opacity-90">
              RFC a firmar: <code className="font-mono font-semibold">{info.rfc_entidad}</code>
            </p>
          )}
          <div className="mt-2 flex gap-2 flex-wrap">
            <Button size="sm" variant="default"
                    onClick={() => window.open(info.portal_firma_url, "_blank", "noopener,noreferrer")}
                    data-testid="cfdi-banner-manifiesto-abrir">
              <FileText className="h-3 w-3 mr-1" />
              Firmar en portal
            </Button>
            <Button size="sm" variant="ghost"
                    onClick={cargar} data-testid="cfdi-banner-manifiesto-refrescar">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Verificar estado ahora
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BannerLegal() {
  return (
    <div data-testid="cfdi-banner-legal"
         className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
      <div className="flex gap-2 items-start">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <p>
          <strong>FELVORA genera y transmite tus CFDI a través de un PAC autorizado.</strong> La
          validez fiscal, la correcta clasificación de tus operaciones y la responsabilidad
          ante el SAT corresponden al sujeto obligado y a su contador. Verifica cada
          comprobante antes de considerarlo definitivo.
        </p>
      </div>
    </div>
  );
}

/**
 * BannerProduccion — nota sutil arriba del PageHeader cuando la entidad
 * tiene `modo_operacion === "produccion"` (facturación activa). Diseño
 * intencionalmente sobrio: sin caja roja ni mayúsculas. La fricción real
 * queda en el diálogo de confirmación previo al timbrado.
 * Cuando `emision_autorizada === false`, se acompaña de una línea con la
 * nota del candado del super_admin.
 */
function BannerProduccion({ estado }) {
  if (estado?.modo_operacion !== "produccion") return null;
  const emisionOn = !!estado?.emision_autorizada;
  return (
    <div data-testid="cfdi-banner-produccion"
         className="text-xs text-slate-600 flex items-center gap-2 border-b border-slate-200 pb-2">
      <span className="inline-flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Facturación activa
      </span>
      <span className="text-slate-400">·</span>
      {emisionOn ? (
        <span>Emisión autorizada por el super_admin.</span>
      ) : (
        <span>Emisión pausada por el super_admin — el botón de timbrado está inhabilitado.</span>
      )}
    </div>
  );
}

/**
 * BadgeModo — chip discreto con el estado del módulo:
 *  - produccion → chip verde "Facturación activa"
 *  - sandbox    → chip amber "Configuración interna"
 *  - vacío (no configurado) → null
 */
function BadgeModo({ modoOperacion, emisionAutorizada }) {
  if (!modoOperacion) return null;
  if (modoOperacion === "produccion") {
    return (
      <div className="flex items-center gap-1">
        <Badge data-testid="cfdi-badge-produccion" variant="outline"
               className="border-emerald-300 bg-emerald-50 text-emerald-800">
          Facturación activa
        </Badge>
        <Badge data-testid="cfdi-badge-emision"
               variant="outline"
               className={emisionAutorizada
                 ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                 : "border-slate-400 bg-slate-100 text-slate-700"}>
          {emisionAutorizada ? "Emisión OK" : "Emisión pausada"}
        </Badge>
      </div>
    );
  }
  // Modo de configuración interna del módulo (equivalente técnico a sandbox Facturapi).
  // Etiqueta neutral — se evita el lenguaje "MODO PRUEBA · SAT no timbra en real".
  return (
    <div className="flex items-center gap-1">
      <Badge data-testid="cfdi-badge-config" variant="outline"
             className="border-amber-400 bg-amber-100 text-amber-900">
        Configuración interna
      </Badge>
      <Badge data-testid="cfdi-badge-emision"
             variant="outline"
             className={emisionAutorizada
               ? "border-amber-400 bg-amber-100 text-amber-900"
               : "border-slate-400 bg-slate-100 text-slate-700"}>
        {emisionAutorizada ? "Emisión OK" : "Emisión BLOQUEADA"}
      </Badge>
    </div>
  );
}

function ChipEstado({ estado }) {
  const map = {
    vigente: "bg-emerald-100 text-emerald-800 border-emerald-300",
    cancelado: "bg-slate-200 text-slate-700 border-slate-300",
    error: "bg-red-100 text-red-800 border-red-300",
    pendiente: "bg-amber-100 text-amber-800 border-amber-300",
  };
  return (
    <Badge className={map[estado] || map.pendiente} variant="outline"
           data-testid={`cfdi-chip-estado-${estado}`}>
      {estado?.toUpperCase() || "—"}
    </Badge>
  );
}

// ============================================================================
// Diálogo de confirmación EXPLÍCITA pre-timbrado.
//   - En sandbox: solo checkbox de "sé lo que hago".
//   - En producción: checkbox + tecleo literal "TIMBRAR" para habilitar el botón.
//     Regla dura del PO — CFDI en producción es SAT REAL, no existe timbrado
//     "real pero de prueba". La fricción evita timbrar por reflejo.
//   - Detección de cliente demo (heurística por prefijo id / RFC de pruebas SAT).
//     Cuando hay heurística + modo producción → alerta MÁXIMA.
// ============================================================================
function _clienteDemoDePreview(preview) {
  const rfc = String(
    preview?.receptor?.rfc || preview?.pago?.rfc || preview?.cliente_rfc || ""
  ).toUpperCase();
  if (["EKU9003173C9", "KAHO641101B39", "XAXX010101000", "XEXX010101000"].includes(rfc)) return true;
  const nom = String(
    preview?.receptor?.nombre || preview?.pago?.cliente_nombre || preview?.cliente_nombre || ""
  ).toLowerCase();
  if (/\b(test|prueba|qa|demo|fixture|e2e)\b/i.test(nom)) return true;
  return false;
}

function DialogConfirmarTimbrar({ open, onOpenChange, preview, tipo, estado,
                                  onConfirm, emitiendo }) {
  const [confirmChk, setConfirmChk] = useState(false);
  const [confirmTxt, setConfirmTxt] = useState("");
  // feb-2026 iter_07-28f — selector de UsoCFDI para override antes de timbrar.
  // El backend ya devuelve `uso_cfdi`, `uso_cfdi_valido` y `usos_validos_para_regimen`
  // en el preview; aquí solo componemos la UX de elección.
  const [usoOverride, setUsoOverride] = useState("");
  useEffect(() => {
    if (open) {
      setConfirmChk(false);
      setConfirmTxt("");
      setUsoOverride(preview?.uso_cfdi || "");
    }
  }, [open, preview?.uso_cfdi]);
  if (!preview) return null;
  const modoOp = estado?.modo_operacion || "sandbox";
  const esProduccion = modoOp === "produccion";
  const emisionOk = !!estado?.emision_autorizada;
  const esDemo = _clienteDemoDePreview(preview);
  const receptorNombre = preview?.receptor?.nombre || preview?.pago?.cliente_nombre
                         || preview?.cliente_nombre || "—";
  const receptorRfc = String(preview?.receptor?.rfc || preview?.pago?.rfc
                              || preview?.cliente_rfc || "—").toUpperCase();
  const monto = preview?.totales?.total ?? preview?.amount;
  const conceptos = (preview?.conceptos || []).map(c => c?.product?.description).filter(Boolean);
  const conceptoResumen = conceptos.length
    ? conceptos.slice(0, 3).join(" · ") + (conceptos.length > 3 ? " …" : "")
    : (tipo === "complemento" ? `Complemento parcialidad #${preview?.installment || "?"}` : "—");

  const tituloTipo = tipo === "ppd" ? "Factura de Ingreso PPD"
                    : tipo === "desembolso" ? "CFDI de comisión de apertura"
                    : tipo === "complemento" ? "Complemento de Pago (P)"
                    : "CFDI de Ingreso PUE";

  // Reglas de habilitación del botón "Timbrar"
  const requiereTexto = esProduccion;
  const textoOK = confirmTxt.trim().toUpperCase() === "TIMBRAR";
  // Bloqueo por datos fiscales del receptor faltantes (feb-2026).
  // El backend expone `preview.faltantes_receptor` desde `cs.validar_receptor(cliente)`
  // (facturacion_sofom.py:2167,2241). Si trae faltantes → bloqueo total: botón, checkbox
  // e input TIMBRAR quedan disabled y se muestra aviso. Corrección solo en expediente.
  const faltantesReceptor = preview?.faltantes_receptor || [];
  const bloqueadoPorReceptor = faltantesReceptor.length > 0;
  // Validación UsoCFDI ↔ régimen del receptor (feb-2026 iter_07-28f).
  // Si el uso elegido no está en `usos_validos_para_regimen`, el botón se bloquea.
  // Regla dura del PO: AVISAR ANTES DE MANDAR AL SAT.
  const usosValidosDelRegimen = preview?.usos_validos_para_regimen || [];
  const clavesValidas = usosValidosDelRegimen.map(u => u.clave);
  const usoActual = (usoOverride || preview?.uso_cfdi || "").toUpperCase();
  const usoOk = clavesValidas.length === 0
                 ? true            // sin régimen capturado → backend usa fallback S01
                 : clavesValidas.includes(usoActual);
  // Conciliación CFDI-vs-Recibo/Crédito (feb-2026 iter_07-28g/h)
  const conciliacionOk = preview?.conciliacion
                          ? preview.conciliacion.cuadra
                          : true;
  const listo = emisionOk && confirmChk && usoOk && conciliacionOk
                 && !bloqueadoPorReceptor
                 && (!requiereTexto || textoOK);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cfdi-confirmar-timbrar" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Confirmar timbrado
            {esProduccion && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                · facturación activa
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Revisa los datos del comprobante antes de enviarlo al PAC.
          </DialogDescription>
        </DialogHeader>

        {esProduccion && (
          <p data-testid="cfdi-confirmar-warn-produccion"
              className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2">
            Este CFDI se transmite al SAT y tiene validez fiscal. Cancelar requiere motivo SAT y puede necesitar aceptación del receptor.
          </p>
        )}

        {esProduccion && esDemo && (
          <div data-testid="cfdi-confirmar-warn-demo"
               className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <div className="flex gap-2 items-start">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" />
              <p>
                <strong>El receptor parece un cliente sintético</strong> (RFC estándar SAT o nombre
                con marcadores QA/test/demo). ¿Confirmas que quieres timbrar un CFDI
                fiscal real a este receptor? Si necesitas configuración interna, cambia el
                modo del módulo con un super_admin antes de continuar.
              </p>
            </div>
          </div>
        )}

        <div className="rounded-lg border p-3 space-y-1 text-sm">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            {tituloTipo}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="col-span-2">
              <div className="text-slate-500">Cliente</div>
              <div data-testid="cfdi-confirmar-cliente" className="font-semibold">{receptorNombre}</div>
            </div>
            <div>
              <div className="text-slate-500">RFC</div>
              <div data-testid="cfdi-confirmar-rfc" className="font-mono">{receptorRfc}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs pt-1">
            <div className="col-span-2">
              <div className="text-slate-500">Concepto(s)</div>
              <div data-testid="cfdi-confirmar-concepto" className="truncate">{conceptoResumen}</div>
            </div>
            <div>
              <div className="text-slate-500">Total (MXN)</div>
              <div data-testid="cfdi-confirmar-monto" className="font-semibold">{mxn(monto)}</div>
            </div>
          </div>
        </div>

        {!emisionOk && (
          <div data-testid="cfdi-confirmar-emision-bloqueada"
               className="rounded-md border border-slate-300 bg-slate-50 p-3 text-xs text-slate-800">
            <strong>La emisión está DESACTIVADA</strong> para esta entidad. Un super_admin
            debe autorizarla explícitamente antes de que puedas timbrar. El botón está
            inhabilitado como candado defensivo (el backend también responde HTTP 403).
          </div>
        )}

        {/* Nota: el aviso visual por faltantes fiscales del receptor vive en el modal
            Preview (cfdi-alert-faltantes-receptor, línea 1657) y ya bloquea el botón que
            abre este segundo modal. Aquí se conserva SÓLO la lógica de defensa en
            profundidad — `bloqueadoPorReceptor` deshabilita checkbox, input TIMBRAR y
            el botón de emisión — sin duplicar el aviso rojo del preview. */}

        {/* Conciliación dura CFDI-vs-Documento origen (feb-2026 iter_07-28g/h).
            Regla PO: el CFDI debe amparar EXACTAMENTE lo mismo que el documento origen
            (recibo del pago o crédito para desembolsos). Si no cuadra, se muestra
            alerta roja y el botón "Timbrar" queda bloqueado. */}
        {preview?.conciliacion && !preview.conciliacion.cuadra && (
          <div data-testid="cfdi-confirmar-conciliacion-alerta"
                className="rounded-md border border-red-400 bg-red-50 p-3 text-xs text-red-900 space-y-1.5">
            <div className="font-semibold">
              El CFDI NO ampara los mismos importes que el
              {preview.conciliacion.tipo_documento_origen === "credito"
                 ? " crédito (comisión sellada al desembolso)"
                 : " recibo del pago"}.
            </div>
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-red-200">
                  <th className="text-left py-0.5">Campo</th>
                  <th className="text-right py-0.5">Origen</th>
                  <th className="text-right py-0.5">CFDI</th>
                  <th className="text-right py-0.5">Δ</th>
                </tr>
              </thead>
              <tbody>
                {preview.conciliacion.diferencias.map((d, i) => (
                  <tr key={i}>
                    <td className="py-0.5">{d.campo}</td>
                    <td className="text-right py-0.5 font-mono">
                      ${(d.recibo || 0).toFixed(2)}
                    </td>
                    <td className="text-right py-0.5 font-mono">
                      ${(d.cfdi || 0).toFixed(2)}
                    </td>
                    <td className="text-right py-0.5 font-mono text-red-800 font-semibold">
                      {d.delta >= 0 ? "+" : ""}{d.delta.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] leading-tight">
              El timbrado está bloqueado. Revisa el desglose del documento origen antes de continuar.
            </p>
          </div>
        )}

        {/* Selector de UsoCFDI + validación contra régimen del receptor
            (feb-2026 iter_07-28f). Regla dura del PO: AVISAR ANTES DE MANDAR AL SAT.
            - Si el uso resuelto no está en usos_validos_para_regimen → alerta visible.
            - El botón "Timbrar" queda deshabilitado hasta que el usuario elija uno válido.
            - Muestra la clave + descripción de cada uso válido para que no tenga que
              buscar el catálogo SAT.
        */}
        {emisionOk && usosValidosDelRegimen.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <Label className="font-semibold">Uso de CFDI (SAT)</Label>
              <div className="text-slate-500">
                Régimen receptor: <span className="font-mono">{preview?.regimen_fiscal_sat_receptor || "(sin capturar)"}</span>
              </div>
            </div>
            {!usoOk && (
              <div data-testid="cfdi-confirmar-uso-alerta"
                    className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
                <div className="font-semibold">
                  El uso <span className="font-mono">{usoActual || "(vacío)"}</span> NO es válido para el régimen fiscal <span className="font-mono">{preview?.regimen_fiscal_sat_receptor || "?"}</span> del receptor.
                </div>
                <div className="mt-0.5">
                  El SAT rechazaría el timbrado. Elige uno de los usos válidos abajo antes de continuar.
                </div>
              </div>
            )}
            <select data-testid="cfdi-confirmar-uso-select"
                     value={usoOverride}
                     onChange={e => setUsoOverride(e.target.value)}
                     className={`w-full rounded-md border px-2 py-1.5 text-xs bg-white ${
                       usoOk ? "border-slate-300" : "border-red-400"}`}>
              {!clavesValidas.includes(usoOverride) && usoOverride && (
                <option value={usoOverride} disabled>
                  {usoOverride} — no válido para este régimen
                </option>
              )}
              {usosValidosDelRegimen.map(u => (
                <option key={u.clave} value={u.clave}>
                  {u.clave} · {u.descripcion}
                </option>
              ))}
            </select>
            {preview?.uso_cfdi_origen && (
              <p className="text-[10px] text-slate-500">
                Origen del uso propuesto:&nbsp;
                {preview.uso_cfdi_origen === "cliente" && "expediente del cliente."}
                {preview.uso_cfdi_origen === "producto" && "default del producto de crédito."}
                {preview.uso_cfdi_origen === "fallback_S01" && "fallback universal (S01) — la SOFOM no ha configurado default."}
              </p>
            )}
          </div>
        )}

        {emisionOk && (
          <div className="space-y-3 pt-1">
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                data-testid="cfdi-confirmar-checkbox"
                type="checkbox"
                checked={confirmChk}
                onChange={e => setConfirmChk(e.target.checked)}
                disabled={bloqueadoPorReceptor}
                className="mt-0.5"
              />
              <span>
                {esProduccion
                  ? <><strong>Confirmo</strong> que este CFDI se emitirá al SAT en modo PRODUCCIÓN
                       y es fiscalmente real. He verificado cliente, RFC, monto y concepto.</>
                  : <>Confirmo que quiero emitir este CFDI. (El módulo está en configuración
                      interna: los CFDI generados aquí no se transmiten al SAT.)</>}
              </span>
            </label>
            {requiereTexto && (
              <div>
                <Label className="text-xs">
                  Para habilitar el botón, escribe la palabra <code className="px-1 bg-slate-100 rounded">TIMBRAR</code>:
                </Label>
                <Input
                  data-testid="cfdi-confirmar-input-timbrar"
                  value={confirmTxt}
                  onChange={e => setConfirmTxt(e.target.value)}
                  placeholder="Escribe TIMBRAR"
                  autoComplete="off"
                  disabled={bloqueadoPorReceptor}
                  className={textoOK
                    ? "border-emerald-400 focus-visible:ring-emerald-500"
                    : "border-slate-300"}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button data-testid="cfdi-confirmar-cancelar" variant="ghost"
                  onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button data-testid="cfdi-confirmar-emitir"
                  onClick={() => onConfirm(usoOverride)}
                  disabled={!listo || emitiendo}
                  className={esProduccion
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : ""}>
            {emitiendo && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {esProduccion ? "Timbrar CFDI real ante el SAT" : "Timbrar CFDI"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TarjetaCsdActual — muestra el CSD activo antes del formulario de subida.
// Regla dura: si Facturapi no responde con estado claro, se pinta "no verificado"
// (fail-safe). Nunca se afirma "vigente" sin confirmación positiva. Permite
// eliminarlo con confirmación (usa DELETE /organizations/{id}/certificate).
// ============================================================================
function TarjetaCsdActual({ onEliminado }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [borrando, setBorrando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sofom/cfdi/organizacion/certificado");
      setInfo(data);
    } catch (e) {
      setInfo({
        has_certificate: false,
        estatus: "no_verificado",
        detalle: e.response?.data?.detail || "No se pudo consultar el estado del CSD.",
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const eliminar = async () => {
    setBorrando(true);
    try {
      await api.delete("/sofom/cfdi/organizacion/certificado");
      toast.success("CSD eliminado. Ya puedes subir uno nuevo.");
      setConfirmDelete(false);
      await cargar();
      onEliminado && onEliminado();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar el CSD.");
    } finally { setBorrando(false); }
  };

  if (loading && !info) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 flex items-center gap-2"
           data-testid="csd-actual-cargando">
        <Loader2 className="h-3 w-3 animate-spin" /> Consultando CSD actual…
      </div>
    );
  }
  if (!info || !info.has_certificate) return null;

  const mapEstado = {
    vigente: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", texto: "✓ Vigente" },
    por_vencer: { color: "bg-amber-100 text-amber-800 border-amber-300",
                  texto: `⚠ Vence en ${info.dias_para_vencer} día${info.dias_para_vencer !== 1 ? "s" : ""}` },
    vencido: { color: "bg-red-100 text-red-800 border-red-300", texto: "✗ Vencido" },
    no_verificado: { color: "bg-slate-100 text-slate-700 border-slate-300", texto: "◯ No verificado" },
  };
  const cfg = mapEstado[info.estatus] || mapEstado.no_verificado;
  const fechaVence = info.expires_at
    ? new Date(info.expires_at).toLocaleDateString("es-MX",
        { day: "2-digit", month: "long", year: "numeric" })
    : "—";

  return (
    <div className="rounded-md border-2 border-slate-800 bg-white p-3 space-y-2"
         data-testid="csd-actual-tarjeta">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">
          Certificado actual de esta organización
        </p>
        <span data-testid={`csd-actual-badge-${info.estatus}`}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${cfg.color}`}>
          {cfg.texto}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-[10px] uppercase text-slate-500 tracking-wider">Número de serie</p>
          <code data-testid="csd-actual-serial"
                className="font-mono text-sm text-slate-900">{info.serial_number}</code>
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500 tracking-wider">RFC</p>
          <code data-testid="csd-actual-rfc"
                className="font-mono text-sm text-slate-900">{info.rfc || "—"}</code>
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500 tracking-wider">Vigente hasta</p>
          <span data-testid="csd-actual-vence" className="text-sm text-slate-900">{fechaVence}</span>
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500 tracking-wider">Cargado el</p>
          <span className="text-sm text-slate-700">
            {info.updated_at
              ? new Date(info.updated_at).toLocaleDateString("es-MX",
                  { day: "2-digit", month: "long", year: "numeric" })
              : "—"}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-600">{info.detalle}</p>
      <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-1 flex-wrap gap-2">
        <p className="text-[11px] text-amber-800">
          <strong>Subir un CSD nuevo reemplazará este.</strong> Los CFDI ya emitidos no se afectan.
        </p>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={cargar} disabled={loading}
                  data-testid="csd-actual-refrescar">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setConfirmDelete(true)}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="csd-actual-eliminar">
            <XCircle className="h-3 w-3 mr-1" />
            Eliminar CSD
          </Button>
        </div>
      </div>

      {/* Confirmación de eliminación */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md" data-testid="csd-eliminar-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-900">Eliminar CSD de la organización</DialogTitle>
            <DialogDescription>
              Esta acción borra el certificado actual (serie{" "}
              <code className="font-mono">{info.serial_number}</code>) de Facturapi.
              <strong className="block mt-2">Una vez eliminado, esta organización NO podrá
              timbrar CFDI hasta que se suba uno nuevo.</strong>
              Los CFDI ya emitidos NO se afectan. La acción quedará registrada en la bitácora.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}
                    disabled={borrando} data-testid="csd-eliminar-cancelar">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={eliminar} disabled={borrando}
                    data-testid="csd-eliminar-confirmar">
              {borrando && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Sí, eliminar CSD
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// BannerCertificadoVence — aviso destacado en /sofom/cfdi cuando faltan ≤60 días
// para vencer el CSD (o ya venció). NO se muestra si estatus=vigente.
// ============================================================================
function BannerCertificadoVence() {
  const [info, setInfo] = useState(null);

  const cargar = async () => {
    try {
      const { data } = await api.get("/sofom/cfdi/organizacion/certificado");
      setInfo(data);
    } catch (_e) {
      setInfo(null);  // fallo silencioso — el banner solo aparece si hay info clara
    }
  };

  useEffect(() => { cargar(); }, []);

  if (!info || !info.has_certificate) return null;
  if (!info.aviso_60d) return null;  // solo vencido o por_vencer

  const esVencido = info.estatus === "vencido";
  const dias = info.dias_para_vencer;
  const fecha = info.expires_at
    ? new Date(info.expires_at).toLocaleDateString("es-MX",
        { day: "2-digit", month: "long", year: "numeric" })
    : "—";

  return (
    <div data-testid={`cfdi-banner-cert-${info.estatus}`}
         className={`rounded-md border-2 p-3 text-sm ${
           esVencido
             ? "border-red-400 bg-red-50 text-red-900"
             : "border-amber-400 bg-amber-50 text-amber-900"
         }`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold">
            {esVencido
              ? `⚠ Tu CSD venció hace ${Math.abs(dias)} día${Math.abs(dias) !== 1 ? "s" : ""}`
              : `⏳ Tu CSD vence en ${dias} día${dias !== 1 ? "s" : ""}`}
            {" "}(el {fecha})
          </p>
          <p className="mt-1 text-xs">
            {esVencido
              ? "Ya no se pueden emitir CFDI. Renueva tu CSD en el portal del SAT y súbelo desde el paso 3 del wizard cuanto antes."
              : "Los CSD vencen a los 4 años. Renueva el tuyo en el portal del SAT y reemplázalo desde el paso 3 del wizard antes de que expire para no perder la capacidad de timbrar."}
          </p>
          <p className="mt-1 text-xs opacity-80">
            RFC: <code className="font-mono font-semibold">{info.rfc || "—"}</code>
            {" · "}Serie: <code className="font-mono">{info.serial_number}</code>
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Paso NUEVO del wizard — Carta Manifiesto (RMF 2.7.2.5 y 2.7.2.7)
//
// Regla dura: la e.firma jamás pasa por FELVORA. El botón abre el portal público
// de Facturapi en pestaña nueva, y el estado se lee EN VIVO por API de
// `pending_steps`. Estado inicial y refresco explícito con "Verificar estado ahora".
// Fail-safe: sólo mostramos "firmada" cuando el backend lo confirma positivamente;
// cualquier otra cosa se muestra como "no verificado" — nunca como firmada por
// omisión.
// ============================================================================
function PasoCartaManifiesto({ estado: _estadoGlobal, onContinuar }) {
  const [info, setInfo] = useState(null);   // {estado,detalle,rfc_entidad,portal_firma_url,...}
  const [loading, setLoading] = useState(false);
  const [rfcCopiado, setRfcCopiado] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sofom/cfdi/manifiesto/estado");
      setInfo(data);
    } catch (e) {
      // Backend inaccesible — mostrar fail-safe como no verificado, no como firmado.
      setInfo({
        estado: "no_verificado",
        detalle: e.response?.data?.detail
          || "No se pudo consultar el estado de la carta. Reintenta.",
        rfc_entidad: "", portal_firma_url: "https://www.facturapi.io/manifiesto",
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const copiarRfc = () => {
    if (!info?.rfc_entidad) return;
    navigator.clipboard?.writeText(info.rfc_entidad);
    setRfcCopiado(true);
    setTimeout(() => setRfcCopiado(false), 2000);
    toast.success("RFC copiado al portapapeles");
  };

  const abrirPortal = () => {
    const url = info?.portal_firma_url || "https://www.facturapi.io/manifiesto";
    window.open(url, "_blank", "noopener,noreferrer");
    // El PO no bloquea el timbrado por esto — el usuario puede continuar sin firmar.
  };

  const badgeEstado = () => {
    if (!info) return null;
    const map = {
      firmada: { color: "bg-emerald-100 text-emerald-800 border-emerald-300", texto: "✓ FIRMADA" },
      pendiente: { color: "bg-amber-100 text-amber-800 border-amber-300", texto: "⚠ PENDIENTE" },
      no_verificado: { color: "bg-slate-100 text-slate-700 border-slate-300", texto: "◯ NO VERIFICADO" },
      sin_organizacion: { color: "bg-slate-100 text-slate-500 border-slate-300", texto: "— SIN ORG" },
    };
    const cfg = map[info.estado] || map.no_verificado;
    return (
      <span data-testid={`manifiesto-badge-${info.estado}`}
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${cfg.color}`}>
        {cfg.texto}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Autorización al PAC — Carta Manifiesto</p>
            <p className="mt-1">
              Autorizas a Facturapi (PAC) a certificar tus CFDI y entregarlos al SAT en tu
              nombre. Este trámite es obligatorio por las <strong>reglas 2.7.2.5 y 2.7.2.7 de la
              Resolución Miscelánea Fiscal 2026</strong> y debe firmarse por cada RFC emisor.
            </p>
            <p className="mt-1 opacity-90">
              <strong>Tu e.firma NUNCA pasa por FELVORA.</strong> La firma ocurre directo
              entre tú y Facturapi. Nosotros sólo consultamos el estado por API.
            </p>
          </div>
        </div>
      </div>

      {/* RFC destacado y copiable, para que el cliente no se equivoque de empresa */}
      {info?.rfc_entidad && (
        <div className="rounded-md border-2 border-slate-800 bg-slate-50 p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
            RFC que debes ingresar al firmar
          </p>
          <div className="mt-1 flex items-center gap-3">
            <code data-testid="manifiesto-rfc-visible"
                  className="font-mono text-2xl font-bold tracking-wide text-slate-900 select-all">
              {info.rfc_entidad}
            </code>
            <Button data-testid="manifiesto-copiar-rfc" size="sm" variant="outline"
                    onClick={copiarRfc}>
              {rfcCopiado ? "✓ Copiado" : "Copiar"}
            </Button>
          </div>
          {info?.razon_social && (
            <p className="mt-1 text-xs text-slate-600">{info.razon_social}</p>
          )}
        </div>
      )}

      {/* Estado actual */}
      <div className="rounded-md border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-700">Estado:</span>
            {loading
              ? <span data-testid="manifiesto-cargando"
                       className="text-xs text-slate-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> consultando…
                </span>
              : badgeEstado()}
            {info?.cache_hit && (
              <span className="text-[10px] text-slate-400" title="Respuesta en cache (<60s)">
                (cache)
              </span>
            )}
          </div>
          <Button data-testid="manifiesto-refrescar" size="sm" variant="ghost"
                  onClick={cargar} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Verificar estado ahora
          </Button>
        </div>
        {info?.detalle && (
          <p data-testid="manifiesto-detalle"
             className="mt-2 text-xs text-slate-600">
            {info.detalle}
          </p>
        )}
      </div>

      {/* Acción principal */}
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="manifiesto-abrir-portal" onClick={abrirPortal}
                variant="default" className="bg-blue-700 hover:bg-blue-800">
          <FileText className="h-4 w-4 mr-2" />
          Abrir portal de firma (pestaña nueva)
        </Button>
        <a href="https://help.facturapi.io/es/articles/13540282-carta-manifiesto-que-es-y-por-que-es-necesaria"
           target="_blank" rel="noopener noreferrer"
           data-testid="manifiesto-link-ayuda"
           className="text-xs text-blue-700 underline">
          ¿Qué firmo exactamente?
        </a>
      </div>

      <p className="text-[11px] text-slate-500">
        Puedes continuar sin firmar. La emisión de CFDI NO se bloquea, pero se
        recomienda firmar cuanto antes conforme a la normativa vigente.
      </p>
    </div>
  );
}

// ============================================================================
// Wizard de configuración
// ============================================================================
function WizardConfig({ estado, onReload, triggerVariant = "outline", triggerSize = "sm",
                       triggerLabel, hideTrigger = false, openControlled, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const open = openControlled !== undefined ? openControlled : openState;
  const setOpen = onOpenChange || setOpenState;
  const [step, setStep] = useState(1);
  const [creando, setCreando] = useState(false);
  // feb-2026 iter_07-24o/p — 3 estados visibles del job async: procesando/listo/error.
  // `jobProgreso` = {estado:'procesando'|'listo'|'error', descripcion, error_mensaje?}
  const [jobProgreso, setJobProgreso] = useState(null);
  const [nombre, setNombre] = useState("");
  const [datos, setDatos] = useState({
    legal_name: "", tax_system: "601", tax_id: "",
    address: { zip: "", street: "", city: "", state: "", country: "MEX" },
  });
  const [csd, setCsd] = useState({ cerFile: null, keyFile: null, password: "" });

  const crearOrg = async () => {
    if (!nombre.trim()) return toast.error("Nombre requerido.");
    setCreando(true);
    setJobProgreso({ estado: "procesando",
                     descripcion: "Iniciando creación de organización en Facturapi…" });
    try {
      // feb-2026 iter_07-25e — Se cambió de POST bloqueante (que reventaba con
      // Cloudflare 524 cuando Facturapi tardaba ~86 s en crear la org) al patrón
      // async con job + polling, idéntico a los pasos 2 y 3. Idempotencia estricta
      // en backend (N1-N4) evita duplicadas al reintentar.
      const { data: jobStart } = await api.post("/sofom/cfdi/organizacion-job", { nombre });
      const jobId = jobStart.job_id;

      // Reuse instantáneo (org ya existía en Facturapi) — no hay que hacer poll.
      if (jobStart.estatus === "listo") {
        toast.info(
          `Organización existente reutilizada: ${(jobStart.organization_id || "").slice(0, 12)}…` +
          (jobStart.legal_configurado ? " (datos fiscales ya guardados)" : "")
        );
        setJobProgreso(null);
        if (jobStart.legal_configurado) setStep(3);
        else setStep(2);
        onReload && onReload();
        return;
      }

      // Reuse de un job en curso (otra pestaña o el mismo usuario reintentando).
      if (jobStart.reused_job) {
        setJobProgreso({ estado: "procesando",
                         descripcion: (jobStart.descripcion_paso
                            || "Reutilizando creación ya en curso en Facturapi…") });
        toast.info("Ya había una creación en curso; se reusa el mismo proceso.");
      } else {
        setJobProgreso({ estado: "procesando",
                         descripcion: (jobStart.descripcion_paso
                            || "Creando organización en Facturapi (~30-90 s)…") });
      }

      // Polling — Facturapi puede tardar hasta ~90 s para crear org.
      const iniciado = Date.now();
      let resultado = null;
      while (Date.now() - iniciado < 180000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: prog } = await api.get(`/sofom/cfdi/organizacion-job/${jobId}`);
          setJobProgreso({ estado: "procesando",
                           descripcion: prog.descripcion_paso
                              || "Creando organización en Facturapi…" });
          if (prog.estatus === "listo") { resultado = prog; break; }
          if (prog.estatus === "error") { resultado = prog; break; }
        } catch (_) { /* seguir intentando */ }
      }

      if (!resultado) {
        setJobProgreso({ estado: "error",
                         descripcion: "La creación está tardando más de lo esperado.",
                         error_mensaje: "Recarga la página; el proceso puede haber terminado en segundo plano. NO hagas clic en 'Crear' de nuevo." });
        toast.error("Creación tardando más de lo esperado. Recarga la página.");
        return;
      }
      if (resultado.estatus === "error") {
        setJobProgreso({ estado: "error",
                         descripcion: "Facturapi rechazó la creación.",
                         error_mensaje: resultado.error_mensaje || "Sin detalle." });
        toast.error(resultado.error_mensaje || "Error creando organización.");
        return;
      }

      // Listo — org creada o reutilizada por N3/N4 en el background.
      const wasReused = !!resultado.reused;
      const orgIdShort = (resultado.organization_id || "").slice(0, 12);
      if (wasReused) {
        toast.info(`Organización reutilizada: ${orgIdShort}…`);
      } else {
        toast.success(`Organización creada: ${orgIdShort}…`);
      }
      setJobProgreso(null);
      onReload && onReload();
      // No hay `legal_configurado` en jobs (solo path reuse instantáneo lo trae) —
      // el paso 2 lo validará al abrirlo.
      setStep(2);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error creando organización.";
      setJobProgreso({ estado: "error", descripcion: "No se pudo iniciar el proceso.",
                        error_mensaje: msg });
      toast.error(msg);
    } finally {
      setCreando(false);
    }
  };

  const configurarLegal = async () => {
    if (!datos.legal_name.trim() || !datos.tax_id.trim() || !datos.address.zip.trim()) {
      return toast.error("Razón social, RFC y CP fiscal son obligatorios.");
    }
    setCreando(true);
    setJobProgreso({ estado: "procesando", descripcion: "Enviando datos fiscales a Facturapi…" });
    try {
      // feb-2026 iter_07-24o — Se cambió de PUT bloqueante (que reventaba con Cloudflare
      // 520 cuando Facturapi tardaba >30s) a job async con polling.
      const { data: jobStart } = await api.post("/sofom/cfdi/organizacion/legal-job", datos);
      const jobId = jobStart.job_id;
      const iniciado = Date.now();
      let resultado = null;
      while (Date.now() - iniciado < 180000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: prog } = await api.get(`/sofom/cfdi/organizacion/legal-job/${jobId}`);
          setJobProgreso({ estado: "procesando", descripcion: prog.descripcion_paso || "Configurando…" });
          if (prog.estatus === "listo") { resultado = prog; break; }
          if (prog.estatus === "error") { resultado = prog; break; }
        } catch (_) { /* seguir intentando el polling */ }
      }
      if (!resultado) {
        setJobProgreso({
          estado: "error",
          descripcion: "La configuración está tardando más de lo esperado.",
          error_mensaje: "Recarga la página; el proceso puede haber terminado en segundo plano.",
        });
        toast.error("La configuración está tardando más de lo esperado.");
      } else if (resultado.estatus === "listo") {
        setJobProgreso({ estado: "listo", descripcion: "Datos fiscales guardados en Facturapi. Continuando al paso 3…" });
        toast.success("Datos fiscales de la organización configurados.");
        await new Promise((r) => setTimeout(r, 1000));
        setStep(3);
      } else {
        setJobProgreso({
          estado: "error",
          descripcion: "Facturapi rechazó los datos fiscales.",
          error_mensaje: resultado.error_mensaje || "Error configurando datos fiscales (sin detalle).",
        });
        toast.error(resultado.error_mensaje || "Error configurando datos fiscales (sin detalle).");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Error iniciando el proceso.";
      setJobProgreso({ estado: "error", descripcion: "No se pudo iniciar el proceso.", error_mensaje: msg });
      toast.error(msg);
    } finally {
      setCreando(false);
      // NO borramos jobProgreso: mantiene el resultado (listo/error) visible.
    }
  };

  const subirCSD = async () => {
    if (!csd.cerFile || !csd.keyFile || !csd.password) {
      return toast.error("Sube .cer, .key y contraseña.");
    }
    setCreando(true);
    setJobProgreso({ estado: "procesando", descripcion: "Enviando CSD a Facturapi para validar…" });
    try {
      const fd = new FormData();
      fd.append("cerFile", csd.cerFile);
      fd.append("keyFile", csd.keyFile);
      fd.append("password", csd.password);
      // feb-2026 iter_07-24p — Mismo patrón async del paso 2 aplicado al paso 3.
      // El endpoint async responde en <1s con job_id; la validación del CSD (que
      // tomaba 20-60s y reventaba con Cloudflare 520) corre en background.
      const { data: jobStart } = await api.post("/sofom/cfdi/organizacion/csd-job", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = jobStart.job_id;
      if (jobStart.rfc_cer && jobStart.rfc_org && jobStart.rfc_cer === jobStart.rfc_org) {
        setJobProgreso({
          estado: "procesando",
          descripcion: `RFC del CSD (${jobStart.rfc_cer}) coincide con la organización. Validando en Facturapi…`,
        });
      }
      const iniciado = Date.now();
      let resultado = null;
      while (Date.now() - iniciado < 180000) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: prog } = await api.get(`/sofom/cfdi/organizacion/csd-job/${jobId}`);
          setJobProgreso({ estado: "procesando", descripcion: prog.descripcion_paso || "Validando CSD…" });
          if (prog.estatus === "listo") { resultado = prog; break; }
          if (prog.estatus === "error") { resultado = prog; break; }
        } catch (_) { /* seguir intentando */ }
      }
      if (!resultado) {
        setJobProgreso({
          estado: "error",
          descripcion: "La validación está tardando más de lo esperado.",
          error_mensaje: "El proceso puede haber terminado en segundo plano. Recarga la página para ver el estado final.",
        });
        toast.error("La validación del CSD está tardando más de lo esperado.");
      } else if (resultado.estatus === "listo") {
        setJobProgreso({
          estado: "listo",
          descripcion: "CSD cargado y validado por Facturapi. Continuando al paso 4 (Carta Manifiesto)…",
        });
        toast.success("CSD cargado y validado por Facturapi.");
        // Pequeña pausa para que el usuario vea el "listo" antes de avanzar
        await new Promise((r) => setTimeout(r, 1200));
        setStep(4);
      } else {
        // Mensaje LEGIBLE del rechazo de Facturapi (RFC no empata, cert revocado,
        // contraseña incorrecta, cert expirado, etc.). Persiste en pantalla.
        setJobProgreso({
          estado: "error",
          descripcion: "Facturapi rechazó el certificado.",
          error_mensaje: resultado.error_mensaje || "Error subiendo CSD (sin detalle).",
        });
        toast.error(resultado.error_mensaje || "Error subiendo CSD (sin detalle).");
      }
    } catch (e) {
      // Errores de validación local (RFC no coincide, archivos vacíos, contraseña faltante)
      const msg = e.response?.data?.detail || "Error iniciando el proceso.";
      setJobProgreso({ estado: "error", descripcion: "No se pudo iniciar el proceso.", error_mensaje: msg });
      toast.error(msg);
    } finally {
      setCreando(false);
      // NO borramos jobProgreso: mantiene el resultado visible en pantalla
      // (procesando/listo/error). Se limpia al hacer clic en "Subir CSD" de nuevo
      // o al cerrar el diálogo.
    }
  };

  const obtenerKey = async () => {
    setCreando(true);
    try {
      await api.post("/sofom/cfdi/organizacion/apikey");
      toast.success("Test Secret Key guardada (cifrada).");
      onReload && onReload();
      setOpen(false);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setCreando(false);
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button data-testid="cfdi-btn-configurar" variant={triggerVariant} size={triggerSize}
                onClick={() => setOpen(true)}>
          <Settings2 className="h-4 w-4 mr-1" />
          {triggerLabel || "Configuración de facturación"}
        </Button>
      )}
      <Dialog open={open} onOpenChange={(o) => {
        setOpen(o);
        // feb-2026 iter_07-24o/p — al abrir/cerrar el wizard limpiamos el estado del job
        // para no arrastrar mensajes de "error" o "listo" de intentos previos.
        if (!o) { setJobProgreso(null); }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Configuración de facturación electrónica
              {estado?.modo_operacion === "produccion" && (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  · facturación activa
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Paso {step} de 5.{" "}
              {estado?.modo_operacion === "produccion" ? (
                <span className="text-slate-600">
                  Los CFDI emitidos en este modo se transmiten al SAT y tienen validez fiscal.
                </span>
              ) : (
                <span>Configuración interna del módulo — los CFDI generados no se transmiten
                  al SAT hasta que el super_admin autorice la facturación activa.</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {!estado?.plataforma_user_key_configurada && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              <strong>La plataforma no tiene configurada la User Key de Facturapi</strong>.
              Contacta al administrador de la plataforma para capturar
              <code className="mx-1 px-1 bg-white rounded">FACTURAPI_USER_TEST_KEY</code>
              en el <code>.env</code> del backend antes de continuar.
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <Label>Nombre de la organización (razón social)</Label>
              <Input data-testid="cfdi-wizard-nombre" value={nombre}
                     onChange={e => setNombre(e.target.value)}
                     placeholder="ej. Financiera de Crédito MX, S.A. de C.V., SOFOM E.N.R." />
              <p className="text-xs text-slate-500">
                Se creará una organización propia en Facturapi para tu SOFOM. El sistema
                guarda el <code>organization_id</code> cifrado (Fernet) por entidad.
              </p>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Razón social legal</Label>
                  <Input data-testid="cfdi-wizard-legal-name"
                         value={datos.legal_name}
                         onChange={e => setDatos({ ...datos, legal_name: e.target.value })} />
                </div>
                <div>
                  <Label>RFC</Label>
                  <Input data-testid="cfdi-wizard-tax-id"
                         value={datos.tax_id}
                         onChange={e => setDatos({ ...datos, tax_id: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label>Régimen fiscal SAT</Label>
                  <Select value={datos.tax_system}
                          onValueChange={v => setDatos({ ...datos, tax_system: v })}>
                    <SelectTrigger data-testid="cfdi-wizard-regimen">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIMENES_SAT.map(r => (
                        <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>CP fiscal (lugar de expedición)</Label>
                  <Input data-testid="cfdi-wizard-cp"
                         value={datos.address.zip}
                         onChange={e => setDatos({ ...datos, address: { ...datos.address, zip: e.target.value } })}
                         maxLength={5} />
                </div>
              </div>
              {/* feb-2026 iter_07-24o/p — chip TRI-COLOR: procesando / listo / error */}
              {jobProgreso && (
                <div data-testid={`cfdi-wizard-legal-progreso-${jobProgreso.estado}`}
                     className={`flex items-start gap-2 rounded-md border-l-4 p-3 text-xs ${
                       jobProgreso.estado === "procesando"
                         ? "border-blue-500 bg-blue-50 text-blue-900"
                         : jobProgreso.estado === "listo"
                           ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                           : "border-red-500 bg-red-50 text-red-900"
                     }`}>
                  {jobProgreso.estado === "procesando" && <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5" />}
                  {jobProgreso.estado === "listo" && <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
                  {jobProgreso.estado === "error" && <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold uppercase tracking-wide text-[11px]">
                      {jobProgreso.estado === "procesando" && "Procesando"}
                      {jobProgreso.estado === "listo" && "Listo"}
                      {jobProgreso.estado === "error" && "Error"}
                    </p>
                    <p className="mt-0.5">{jobProgreso.descripcion}</p>
                    {jobProgreso.error_mensaje && (
                      <p className="mt-1 rounded bg-white/60 px-2 py-1 font-mono text-[11px] break-words">
                        {jobProgreso.error_mensaje}
                      </p>
                    )}
                    {jobProgreso.estado === "procesando" && (
                      <p className="mt-1 opacity-80">
                        Puedes esperar aquí; el proceso corre en segundo plano y NO crea organizaciones duplicadas.
                      </p>
                    )}
                    {jobProgreso.estado === "error" && (
                      <p className="mt-1 opacity-80">
                        Corrige los datos y vuelve a presionar <strong>Guardar datos fiscales</strong>. Reintentar es seguro.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3">
              {/* Muestra el CSD activo (si hay) antes del formulario de subida.
                  El componente se refresca solo tras eliminar; en el flujo natural
                  del wizard, subir un CSD nuevo dispara el reemplazo automáticamente. */}
              <TarjetaCsdActual />
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Sube el Certificado de Sello Digital (CSD) vigente de tu SOFOM.</strong>{" "}
                El archivo <code>.cer</code>, <code>.key</code> y la contraseña son los emitidos
                por el SAT para tu RFC. Un CSD incorrecto o revocado impedirá el timbrado o
                generará comprobantes inválidos.
                {estado?.modo_operacion !== "produccion" && (
                  <span className="block mt-1 text-[11px] opacity-80">
                    Nota técnica: para validación interna existe un CSD demo público del SAT
                    (RFC EKU9003173C9, contraseña <code>12345678a</code>). No usarlo en producción.
                  </span>
                )}
              </div>
              <div>
                <Label>Archivo .cer</Label>
                <Input data-testid="cfdi-wizard-cer" type="file" accept=".cer"
                       onChange={e => setCsd({ ...csd, cerFile: e.target.files?.[0] })} />
              </div>
              <div>
                <Label>Archivo .key</Label>
                <Input data-testid="cfdi-wizard-key" type="file" accept=".key"
                       onChange={e => setCsd({ ...csd, keyFile: e.target.files?.[0] })} />
              </div>
              <div>
                <Label>Contraseña del CSD</Label>
                <Input data-testid="cfdi-wizard-password" type="password"
                       value={csd.password}
                       onChange={e => setCsd({ ...csd, password: e.target.value })} />
              </div>
              {/* feb-2026 iter_07-24p — chip TRI-COLOR: procesando / listo / error */}
              {jobProgreso && (
                <div data-testid={`cfdi-wizard-csd-progreso-${jobProgreso.estado}`}
                     className={`flex items-start gap-2 rounded-md border-l-4 p-3 text-xs ${
                       jobProgreso.estado === "procesando"
                         ? "border-blue-500 bg-blue-50 text-blue-900"
                         : jobProgreso.estado === "listo"
                           ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                           : "border-red-500 bg-red-50 text-red-900"
                     }`}>
                  {jobProgreso.estado === "procesando" && <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5" />}
                  {jobProgreso.estado === "listo" && <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
                  {jobProgreso.estado === "error" && <XCircle className="h-4 w-4 shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold uppercase tracking-wide text-[11px]">
                      {jobProgreso.estado === "procesando" && "Procesando"}
                      {jobProgreso.estado === "listo" && "Listo"}
                      {jobProgreso.estado === "error" && "Error"}
                    </p>
                    <p className="mt-0.5">{jobProgreso.descripcion}</p>
                    {jobProgreso.error_mensaje && (
                      <p className="mt-1 rounded bg-white/60 px-2 py-1 font-mono text-[11px] break-words">
                        {jobProgreso.error_mensaje}
                      </p>
                    )}
                    {jobProgreso.estado === "procesando" && (
                      <p className="mt-1 opacity-80">
                        Este paso puede tardar 20–60 segundos. NO recargues ni cierres la ventana;
                        el proceso corre en segundo plano y no deja la organización a medias.
                      </p>
                    )}
                    {jobProgreso.estado === "error" && (
                      <p className="mt-1 opacity-80">
                        Corrige el problema indicado arriba y vuelve a presionar <strong>Subir CSD</strong>.
                        Reintentar es seguro: NO duplica organizaciones.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <PasoCartaManifiesto estado={estado} onContinuar={() => setStep(5)} />
          )}
          {step === 5 && (
            <div className="text-sm space-y-3">
              <p>Obtén y guarda la <strong>Secret Key</strong> de la organización.
              Se almacena cifrada por entidad en <code>integraciones_credenciales</code>.</p>
              <p>
                Con esta llave el módulo podrá emitir CFDI en{" "}
                {estado?.modo_operacion === "produccion"
                  ? "modo PRODUCCIÓN (SAT real)"
                  : "modo de configuración interna (sin envío al SAT)"}.
              </p>
              {estado?.modo_operacion === "produccion" && !estado?.emision_autorizada && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                  <strong>La emisión sigue BLOQUEADA por seguridad.</strong> Un super_admin
                  debe autorizarla explícitamente en <code>PUT /sofom/cfdi/emision-autorizada</code>{" "}
                  antes de que cualquier operador pueda timbrar en producción.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {step === 1 && (
              <Button data-testid="cfdi-wizard-crear-org" onClick={crearOrg}
                      disabled={creando || !estado?.plataforma_user_key_configurada}>
                {creando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Crear organización
              </Button>
            )}
            {step === 2 && (
              <Button data-testid="cfdi-wizard-configurar-legal" onClick={configurarLegal}
                      disabled={creando}>
                {creando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Guardar datos fiscales
              </Button>
            )}
            {step === 3 && (
              <Button data-testid="cfdi-wizard-subir-csd" onClick={subirCSD} disabled={creando}>
                {creando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Subir CSD
              </Button>
            )}
            {step === 4 && (
              <Button data-testid="cfdi-wizard-continuar-manifiesto"
                      onClick={() => setStep(5)}
                      variant="secondary">
                Continuar al paso 5
              </Button>
            )}
            {step === 5 && (
              <Button data-testid="cfdi-wizard-obtener-key" onClick={obtenerKey} disabled={creando}>
                {creando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Obtener y guardar Test Key
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// Preview + timbrado (soporta PUE pago, desembolso, factura PPD y complemento)
// ============================================================================
function PreviewTimbrado({ pagoId, credId, tipo, integranteClienteId, estado, onEmitido }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emitiendo, setEmitiendo] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Progreso del job async (feb-2026 iter_07-28e): mantiene un mensaje visible
  // mientras Facturapi timbra + descarga XML/PDF (hasta ~90 s). Evita corte
  // de Cloudflare y da feedback continuo al usuario.
  const [progreso, setProgreso] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setBusy(true);
      try {
        let path;
        if (tipo === "ppd") path = `/sofom/cfdi/preview/factura_ppd/${credId}`;
        else if (tipo === "desembolso") path = `/sofom/cfdi/preview/desembolso/${credId}`;
        else if (tipo === "complemento") path = `/sofom/cfdi/preview/complemento/${pagoId}`;
        else path = `/sofom/cfdi/preview/pago/${pagoId}`;
        // FASE B (feb-2026) — Desembolso grupal, y Bloque 3 (feb-2026) — pago/complemento
        // grupal, y Bloque 2 (feb-2026) — factura PPD por integrante: propagar el
        // integrante_cliente_id como query param para armar el preview con la ficha
        // fiscal (RFC/régimen/CP/uso CFDI) del integrante correcto.
        const params = integranteClienteId ? { integrante_cliente_id: integranteClienteId }
                                             : undefined;
        const { data } = await api.get(path, { params });
        if (!cancel) setPreview(data);
      } catch (e) {
        if (!cancel) toast.error(e.response?.data?.detail || "Error");
      } finally {
        if (!cancel) setBusy(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [pagoId, credId, tipo, integranteClienteId]);

  // Mapeo del `tipo` UI al `tipo` del backend async
  const tipoBackend = tipo === "ppd" ? "factura_ppd"
                     : tipo === "desembolso" ? "desembolso"
                     : tipo === "complemento" ? "complemento"
                     : "pago";

  const timbrar = async (usoOverride = "") => {
    setEmitiendo(true);
    setProgreso({ estatus: "queued",
                  descripcion: "Enviando solicitud a Facturapi…" });
    try {
      const body = {
        tipo: tipoBackend,
        pago_id: (tipo === "ppd" || tipo === "desembolso") ? null : pagoId,
        credito_id: (tipo === "ppd" || tipo === "desembolso") ? credId : null,
        uso_cfdi_override: (usoOverride || "").toUpperCase(),
        // Bloque 2/3 (feb-2026) — integrante_cliente_id para: desembolso grupal (Fase B),
        // factura PPD por integrante (Bloque 2), pago grupal por integrante (Bloque 3),
        // complemento por integrante (Bloque 3). Individual → null (se ignora).
        integrante_cliente_id: integranteClienteId || null,
      };
      // 1) Dispatch: crea job en <1 s (nunca alcanza el corte de Cloudflare a 100 s)
      const { data: dispatch } = await api.post("/sofom/cfdi/emitir-async", body);
      // Idempotencia: si el CFDI ya existía, dispatch responde `done` de inmediato
      if (dispatch.estatus === "done" && !dispatch.job_id) {
        toast.success(dispatch.duplicado
          ? `Ya existía un CFDI vigente · UUID ${dispatch.cfdi?.uuid_sat?.slice(0, 12) || "—"}… No se timbró de nuevo.`
          : `CFDI · UUID ${dispatch.cfdi?.uuid_sat?.slice(0, 12) || "—"}…`);
        setProgreso(null);
        setEmitiendo(false);
        onEmitido && onEmitido();
        return;
      }
      const jobId = dispatch.job_id;
      setProgreso({ estatus: dispatch.estatus,
                    descripcion: dispatch.descripcion_paso || "En curso…" });
      // 2) Polling cada 2 s hasta done|error (máx 90 intentos = 3 min).
      let intentos = 0;
      const maxIntentos = 90;
      while (intentos < maxIntentos) {
        await new Promise(r => setTimeout(r, 2000));
        intentos += 1;
        try {
          const { data: st } = await api.get(`/sofom/cfdi/timbrar-job/${jobId}`);
          setProgreso({ estatus: st.estatus,
                        descripcion: st.descripcion_paso || "En curso…" });
          if (st.estatus === "done") {
            const uuid = st.cfdi_uuid || st.resultado?.cfdi?.uuid_sat;
            toast.success(st.duplicado
              ? `Ya existía un CFDI vigente · UUID ${(uuid || "—").slice(0, 12)}…`
              : `CFDI timbrado · UUID ${(uuid || "—").slice(0, 12)}…`);
            onEmitido && onEmitido();
            setProgreso(null);
            return;
          }
          if (st.estatus === "error") {
            toast.error(st.mensaje_error || "Facturapi rechazó el timbrado.");
            setProgreso({ estatus: "error",
                          descripcion: st.mensaje_error || "Error timbrando." });
            return;
          }
        } catch (pollErr) {
          // Errores transitorios de polling — se reintentan hasta el max
          if (intentos >= maxIntentos - 3) {
            toast.error("Timeout esperando resultado del timbrado. Usa 'Reconciliar' para verificar si el CFDI YA se emitió.");
            setProgreso({ estatus: "error",
                          descripcion: "Timeout de polling. Reconciliar antes de reintentar." });
            return;
          }
        }
      }
      toast.error("Se alcanzó el máximo de espera. Usa 'Reconciliar' para verificar antes de reintentar.");
      setProgreso({ estatus: "error",
                    descripcion: "Timeout de polling. Reconciliar antes de reintentar." });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error iniciando el timbrado.");
      setProgreso(null);
    } finally {
      setEmitiendo(false);
    }
  };

  // Botón de reconciliación (READ-ONLY) — verifica si el CFDI ya se emitió en
  // Facturapi antes de reintentar. Sólo disponible para pagos (tipo === default o pago).
  const reconciliar = async () => {
    try {
      const { data } = await api.get(`/sofom/cfdi/reconciliar/pago/${pagoId}`);
      const en_mongo = !!data.en_mongo;
      const candidatos = data.facturapi?.candidatos || [];
      const posibles = candidatos.filter(c => (c.match_score || 0) >= 2);
      if (en_mongo) {
        toast.success(`CFDI YA registrado en FELVORA · UUID ${data.en_mongo.uuid_sat?.slice(0, 12) || "—"}…`);
      } else if (posibles.length) {
        const c = posibles[0];
        toast.warning(`Posible CFDI huérfano en Facturapi · UUID ${(c.uuid || "—").slice(0, 12)}… total ${c.total}. Revisa antes de reintentar.`);
      } else {
        toast.info(data.recomendacion || "Sin duplicados detectados.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error consultando Facturapi.");
    }
  };

  if (busy) return (
    <div className="p-4 text-center text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Cargando preview…
    </div>
  );
  if (!preview) return null;

  // Complemento tiene esquema distinto (no tiene 'conceptos')
  if (tipo === "complemento") {
    return (
      <>
        <PreviewComplemento preview={preview} emitiendo={emitiendo}
                            estado={estado} tipo={tipo}
                            onTimbrar={() => setShowConfirm(true)} />
        <DialogConfirmarTimbrar
          open={showConfirm} onOpenChange={setShowConfirm}
          preview={preview} tipo={tipo} estado={estado}
          onConfirm={async (usoOverride) => { setShowConfirm(false); await timbrar(usoOverride); }}
          emitiendo={emitiendo}
        />
      </>
    );
  }

  const bloqueadoReceptor = (preview.faltantes_receptor || []).length > 0;
  const bloqueadoEmisor = (preview.faltantes_emisor || []).length > 0;
  const noTimbrable = preview.no_timbrable;
  const noAplica = preview.no_aplica;

  return (
    <div className="space-y-4">
      <BadgeModo modoOperacion={preview.modo_operacion || (preview.modo_prueba ? "sandbox" : "produccion")}
                 emisionAutorizada={preview.emision_autorizada} />

      {tipo === "ppd" && preview.factura_existente && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
          Este crédito YA tiene factura PPD vigente (UUID {preview.factura_existente.uuid_sat?.slice(0, 12)}…, saldo pendiente {mxn(preview.factura_existente.saldo_pendiente)}). No se emitirá otra.
        </div>
      )}

      {noAplica && (
        <div className="rounded-md bg-slate-100 border p-3 text-sm text-slate-700">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          {preview.motivo || "No aplica."}
        </div>
      )}

      {noTimbrable && !noAplica && (
        <div className="rounded-md bg-slate-100 border p-3 text-sm text-slate-700">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          {preview.motivo_no_timbrable || "Sin conceptos timbrables."}
        </div>
      )}

      {bloqueadoReceptor && (
        <div data-testid="cfdi-alert-faltantes-receptor"
             className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <strong>El cliente no tiene datos fiscales completos.</strong> Falta:{" "}
          {preview.faltantes_receptor.join(", ")}. Captura régimen fiscal y CP fiscal EN
          la ficha del cliente para poder timbrar.
        </div>
      )}
      {bloqueadoEmisor && (
        <div data-testid="cfdi-alert-faltantes-emisor"
             className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <strong>Configuración de facturación incompleta:</strong>{" "}
          {preview.faltantes_emisor.join(", ")}.
        </div>
      )}

      {tipo === "ppd" && preview.leyenda_ppd && !noAplica && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
          <FileStack className="h-4 w-4 inline mr-1" /> {preview.leyenda_ppd}
        </div>
      )}

      {/* Resumen fiscal — refuerza la clasificación IVA antes de timbrar. */}
      {preview.resumen_clasificacion_iva && (
        <div data-testid="cfdi-resumen-clasificacion-iva"
             className={`rounded-md border p-3 text-xs ${
               preview.regimen_iva_credito === "gravado"
                 ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                 : "border-slate-300 bg-slate-50 text-slate-800"
             }`}>
          <Info className="h-4 w-4 inline mr-1" />
          {preview.resumen_clasificacion_iva}
        </div>
      )}

      <div className="rounded-lg border p-4 space-y-2 text-sm">
        <div className="font-semibold text-primary">
          Desglose del CFDI · {tipo === "ppd" ? "Factura de Ingreso PPD" : tipo === "desembolso" ? "Comisión de apertura (desembolso)" : "CFDI de Ingreso PUE"}
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {preview.pago && (
            <>
              <div><span className="text-slate-500">Recibo:</span> {preview.pago.recibo || "—"}</div>
              <div><span className="text-slate-500">Fecha:</span> {fdate(preview.pago.fecha_pago)}</div>
              <div><span className="text-slate-500">Cliente:</span> {preview.pago.cliente_nombre}</div>
              <div><span className="text-slate-500">Crédito:</span> {preview.pago.numero_credito}</div>
            </>
          )}
          {!preview.pago && (
            <>
              <div><span className="text-slate-500">Crédito:</span> {preview.numero_credito}</div>
              <div><span className="text-slate-500">Cliente:</span> {preview.cliente_nombre}</div>
            </>
          )}
          <div><span className="text-slate-500">Método SAT:</span> {preview.metodo}</div>
          <div><span className="text-slate-500">Forma pago SAT:</span> {preview.forma_pago_sat}</div>
          <div><span className="text-slate-500">Uso CFDI:</span> {preview.uso_cfdi}</div>
          <div><span className="text-slate-500">Régimen IVA crédito:</span> {preview.regimen_iva_credito || "—"}</div>
        </div>
        <div className="border-t pt-2 space-y-1">
          {(preview.conceptos || []).map((c, i) => (
            <div key={i} className="flex justify-between text-xs">
              <span>{c.product?.description}</span>
              <span>{mxn(c.product?.price)}</span>
            </div>
          ))}
          <div className="flex justify-between font-medium border-t pt-1">
            <span>Subtotal</span><span>{mxn(preview.totales?.subtotal)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span>IVA</span><span>{mxn(preview.totales?.iva)}</span>
          </div>
          <div className="flex justify-between font-semibold text-primary">
            <span>TOTAL</span><span>{mxn(preview.totales?.total)}</span>
          </div>
        </div>
      </div>

      <div data-testid="cfdi-aviso-timbrado"
           className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong>Una vez timbrado, el CFDI se envía al SAT y adquiere validez fiscal.</strong>{" "}
        Revisa importes, datos del receptor y conceptos antes de confirmar. La cancelación
        posterior está sujeta a las reglas del SAT y puede requerir aceptación del receptor.
      </div>

      <div className="flex justify-end gap-2">
        {progreso && (
          <div data-testid="cfdi-progreso-async"
                className={`flex-1 rounded-md border p-3 text-xs ${
                  progreso.estatus === "error"
                    ? "border-red-300 bg-red-50 text-red-900"
                    : "border-sky-300 bg-sky-50 text-sky-900"
                }`}>
            {progreso.estatus !== "error" && (
              <Loader2 className="h-3 w-3 mr-1 inline animate-spin" />)}
            <strong className="font-semibold uppercase">{progreso.estatus}</strong> · {progreso.descripcion}
            {progreso.estatus !== "error" && (
              <p className="mt-1 text-[10px] opacity-75">
                No cierres esta ventana. El timbrado corre en el servidor y sigue aunque tu conexión se corte.
              </p>
            )}
          </div>
        )}
        {tipo !== "ppd" && tipo !== "desembolso" && tipo !== "complemento" && (
          <Button variant="outline" size="sm"
                  data-testid="cfdi-btn-reconciliar"
                  onClick={reconciliar}
                  disabled={emitiendo}>
            Reconciliar con Facturapi
          </Button>
        )}
        <Button
          data-testid="cfdi-btn-timbrar"
          onClick={() => setShowConfirm(true)}
          disabled={emitiendo || noTimbrable || noAplica || bloqueadoReceptor || bloqueadoEmisor
                    || (tipo === "ppd" && preview.factura_existente)
                    || (estado && !estado.emision_autorizada)}
        >
          {emitiendo && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {tipo === "ppd" ? "Confirmar y emitir factura PPD" : "Confirmar y timbrar CFDI"}
        </Button>
      </div>
      <DialogConfirmarTimbrar
        open={showConfirm} onOpenChange={setShowConfirm}
        preview={preview} tipo={tipo} estado={estado}
        onConfirm={async (usoOverride) => { setShowConfirm(false); await timbrar(usoOverride); }}
        emitiendo={emitiendo}
      />
    </div>
  );
}

function PreviewComplemento({ preview, emitiendo, onTimbrar, estado }) {
  if (preview.no_aplica) {
    return (
      <div className="rounded-md bg-slate-100 border p-3 text-sm text-slate-700">
        <AlertTriangle className="h-4 w-4 inline mr-1" />
        {preview.motivo || "No aplica."}
      </div>
    );
  }
  const bloqueado = (preview.faltantes_receptor || []).length > 0;
  return (
    <div className="space-y-4">
      {preview.cfdi_pago_uuid_actual && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
          Este pago ya tiene complemento (UUID {String(preview.cfdi_pago_uuid_actual).slice(0, 12)}…). No se emitirá otro.
        </div>
      )}
      {bloqueado && (
        <div data-testid="cfdi-alert-faltantes-receptor"
             className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <strong>El cliente no tiene datos fiscales completos.</strong> Falta:{" "}
          {preview.faltantes_receptor.join(", ")}.
        </div>
      )}
      {preview.excede_saldo && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          <strong>El monto del pago excede el saldo pendiente de la factura padre.</strong>{" "}
          Pago: {mxn(preview.amount)}. Saldo: {mxn(preview.last_balance)}. Revisa la
          aplicación del pago o cancela y re-emite la factura padre.
        </div>
      )}

      <div className="rounded-lg border p-4 space-y-2 text-sm">
        <div className="font-semibold text-primary">Complemento de Pago (CFDI tipo P)</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-slate-500">Recibo:</span> {preview.recibo}</div>
          <div><span className="text-slate-500">Cliente:</span> {preview.cliente_nombre}</div>
          <div><span className="text-slate-500">Factura padre:</span>
            <span className="font-mono ml-1">{String(preview.factura_padre_uuid || "").slice(0, 20)}…</span></div>
          <div><span className="text-slate-500">Parcialidad:</span> #{preview.installment}</div>
          <div><span className="text-slate-500">Saldo previo:</span> {mxn(preview.last_balance)}</div>
          <div><span className="text-slate-500">Monto pago:</span> {mxn(preview.amount)}</div>
          <div><span className="text-slate-500">Saldo después:</span>
            <span className="ml-1 font-semibold">{mxn(Math.max(0, preview.last_balance - preview.amount))}</span></div>
          <div><span className="text-slate-500">Forma SAT:</span> {preview.forma_pago_sat}</div>
          <div><span className="text-slate-500">Tax object:</span> {preview.tax_object}</div>
          <div><span className="text-slate-500">Base IVA:</span> {mxn(preview.base_iva)}</div>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <strong>El Complemento de Pago se envía al SAT y adquiere validez fiscal.</strong> El
        saldo de la factura padre se reduce automáticamente. Verifica el monto, la
        parcialidad y la forma de pago antes de confirmar.
      </div>

      <div className="flex justify-end gap-2">
        <Button
          data-testid="cfdi-btn-timbrar-complemento"
          onClick={onTimbrar}
          disabled={emitiendo || bloqueado || preview.excede_saldo || preview.no_timbrable
                    || preview.cfdi_pago_uuid_actual
                    || (estado && !estado.emision_autorizada)}
        >
          {emitiendo && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Emitir Complemento de Pago
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Diálogo de cancelación
// ============================================================================
function DialogCancelar({ cfdi, onCancelado, open, onOpenChange }) {
  const [motivo, setMotivo] = useState("02");
  const [substitution, setSubstitution] = useState("");
  const [busy, setBusy] = useState(false);
  const cancelar = async () => {
    setBusy(true);
    try {
      await api.post(`/sofom/cfdi/cancelar/${cfdi.id}`,
        { motive: motivo, substitution: motivo === "01" ? substitution : undefined });
      toast.success("CFDI cancelado.");
      onCancelado && onCancelado();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancelar CFDI</DialogTitle>
          <DialogDescription>
            UUID {cfdi?.uuid_sat}. Elige el motivo SAT.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Motivo SAT</Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger data-testid="cfdi-cancelar-motivo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="01">01 · Con relación (requiere UUID sustituto)</SelectItem>
                <SelectItem value="02">02 · Con errores sin relación</SelectItem>
                <SelectItem value="03">03 · No se llevó a cabo la operación</SelectItem>
                <SelectItem value="04">04 · Operación nominativa relacionada en global</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {motivo === "01" && (
            <div>
              <Label>UUID del CFDI que sustituye</Label>
              <Input data-testid="cfdi-cancelar-uuid" value={substitution}
                     onChange={e => setSubstitution(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="cfdi-cancelar-confirmar" onClick={cancelar} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Cancelar CFDI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Página principal
// ============================================================================
export default function CFDIsofom() {
  const { user } = useAuth();
  const [estado, setEstado] = useState(null);
  const [tab, setTab] = useState("timbrables");
  const [timbrables, setTimbrables] = useState([]);
  const [creditosPPD, setCreditosPPD] = useState([]);
  const [desembolsosPendientes, setDesembolsosPendientes] = useState([]);  // feb-2026 iter_07-28h
  const [cfdiList, setCfdiList] = useState([]);
  const [contadores, setContadores] = useState({});
  const [selectedPago, setSelectedPago] = useState(null);
  const [selectedCred, setSelectedCred] = useState(null);
  const [selectedIntegrante, setSelectedIntegrante] = useState(null);  // Fase B (feb-2026)
  const [previewTipo, setPreviewTipo] = useState("pago");  // pago | desembolso | ppd | complemento
  const [showPreview, setShowPreview] = useState(false);
  const [cancelCfdi, setCancelCfdi] = useState(null);
  const [sustituirCfdi, setSustituirCfdi] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("all");

  const reload = async () => {
    try {
      const [e, t, cp, dp, c] = await Promise.all([
        api.get("/sofom/cfdi/estado"),
        api.get("/sofom/cfdi/timbrables/pagos"),
        api.get("/sofom/cfdi/timbrables/creditos_ppd"),
        api.get("/sofom/cfdi/timbrables/desembolsos"),  // Gap 1 iter_07-28h
        api.get("/sofom/cfdi", { params: { estado: filtroEstado === "all" ? undefined : filtroEstado } }),
      ]);
      setEstado(e.data);
      setTimbrables(t.data.pagos || []);
      setCreditosPPD(cp.data.creditos || []);
      setDesembolsosPendientes(dp.data.creditos || []);
      setCfdiList(c.data.cfdi || []);
      setContadores(c.data.contadores || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filtroEstado]);

  const timbrarPago = (p) => {
    setSelectedPago(p.id);
    setSelectedCred(null);
    // Bloque 3 (feb-2026) — En grupal, p.integrante_cliente_id trae la porción a timbrar
    setSelectedIntegrante(p.integrante_cliente_id || null);
    setPreviewTipo(p.necesita === "complemento" ? "complemento" : "pago");
    setShowPreview(true);
  };
  // Emitir CFDI de comisión de apertura (desembolso) — Gap 1 iter_07-28h.
  // Fase B (feb-2026): en grupal, row.integrante_cliente_id trae la porción a timbrar.
  const emitirComisionDesembolso = (row) => {
    setSelectedCred(row.credito_id);
    setSelectedPago(null);
    setSelectedIntegrante(row.integrante_cliente_id || null);
    setPreviewTipo("desembolso");
    setShowPreview(true);
  };
  const emitirFacturaPPD = (credito) => {
    setSelectedCred(credito.credito_id);
    setSelectedPago(null);
    setSelectedIntegrante(null);
    setPreviewTipo("ppd");
    setShowPreview(true);
  };
  const sustituir = async () => {
    if (!sustituirCfdi) return;
    try {
      const { data } = await api.post(`/sofom/cfdi/sustituir/${sustituirCfdi.id}`, {});
      toast.success(`Sustituto emitido: ${data.sustituto.uuid_sat?.slice(0, 12)}…`);
      setSustituirCfdi(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const descargarXml = async (id) => {
    try {
      const r = await api.get(`/sofom/cfdi/${id}/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cfdi_${id}.xml`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descargar el XML");
    }
  };
  const descargarPdf = async (id) => {
    try {
      const r = await api.get(`/sofom/cfdi/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `cfdi_${id}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descargar el PDF");
    }
  };
  const enviarCorreo = async (id) => {
    try {
      await api.post(`/sofom/cfdi/${id}/enviar`, {});
      toast.success("CFDI enviado por correo al cliente.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // Acuse de cancelación SAT (feb-2026). Solo disponible en CFDI cancelados cuyo
  // `acuse_xml_key` / `acuse_pdf_key` ya está persistido en GridFS. Si no está,
  // se muestra "Acuse pendiente" con botón para reintentar (POST /acuse/reintentar).
  const descargarAcuseXml = async (id) => {
    try {
      const r = await api.get(`/sofom/cfdi/${id}/acuse/xml`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = `acuse_cancelacion_${id}.xml`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descargar el acuse XML");
    }
  };
  const descargarAcusePdf = async (id) => {
    try {
      const r = await api.get(`/sofom/cfdi/${id}/acuse/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `acuse_cancelacion_${id}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo descargar el acuse PDF");
    }
  };
  const reintentarAcuse = async (id) => {
    try {
      const { data } = await api.post(`/sofom/cfdi/${id}/acuse/reintentar`);
      const st = data?.acuse?.status;
      if (st === "guardado" || st === "ya_guardado") {
        toast.success("Acuse SAT descargado y guardado. Refrescando la tabla…");
      } else if (st === "pendiente") {
        toast.info(`Acuse aún no disponible: ${data.acuse.motivo || "SAT no ha aceptado la cancelación"}.`);
      } else {
        toast.error(`No se pudo obtener el acuse: ${data.acuse?.error || "error desconocido"}.`);
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reintentar el acuse");
    }
  };

  const [openWizard, setOpenWizard] = useState(false);

  const canEdit = ["admin_entidad", "contabilidad_fiscal"].includes(user?.role);

  return (
    <div data-testid="cfdi-sofom-page" className="p-4 space-y-4">
      <PageHeader
        title="CFDI SOFOM"
        subtitle="Facturación electrónica CFDI 4.0 vía PAC (Facturapi) · PUE + PPD con complemento de pago"
      >
        <BadgeModo modoOperacion={estado?.modo_operacion}
                   emisionAutorizada={estado?.emision_autorizada} />
        {canEdit && estado?.configurado && (
          <WizardConfig
            estado={estado} onReload={reload}
            triggerVariant="ghost" triggerSize="sm"
            triggerLabel="Editar configuración"
            openControlled={openWizard}
            onOpenChange={setOpenWizard}
          />
        )}
      </PageHeader>

      <BannerProduccion estado={estado} />
      <BannerLegal />
      {estado?.configurado && <BannerCertificadoVence />}
      {estado?.configurado && <BannerManifiesto />}

      {/* SETUP CARD — se muestra sólo cuando la configuración NO está completa.
          Consolida en un único bloque: aviso + advertencia de User Key + botón único
          "Iniciar configuración" que abre el wizard EN LA MISMA VISTA (modal). */}
      {!estado?.configurado && canEdit && (
        <div data-testid="cfdi-alert-no-configurado"
             className="rounded-lg border border-amber-300 bg-amber-50 p-5">
          <div className="flex gap-3 items-start">
            <ShieldAlert className="h-6 w-6 shrink-0 mt-0.5 text-amber-700" />
            <div className="flex-1 space-y-3">
              <div>
                <div className="text-base font-semibold text-amber-900">
                  Configura la facturación electrónica para comenzar a timbrar
                </div>
                <p className="mt-1 text-sm text-amber-900">
                  Aún no has creado tu organización en Facturapi ni subido el CSD del SAT.
                  Configúralo aquí mismo para poder emitir CFDI de este módulo.
                </p>
              </div>

              <ol className="text-xs text-amber-900 space-y-1 list-decimal ml-4">
                <li>Crear organización propia en Facturapi (multi-tenant).</li>
                <li>Capturar datos fiscales de la SOFOM (RFC, régimen, CP fiscal).</li>
                <li>Subir el Certificado de Sello Digital (.cer + .key + contraseña).</li>
                <li>Firmar la Carta Manifiesto (RMF 2026 · reglas 2.7.2.5 y 2.7.2.7).</li>
                <li>Guardar la Test Secret Key cifrada por entidad.</li>
              </ol>

              {estado && !estado.plataforma_user_key_configurada && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800"
                     data-testid="cfdi-alert-user-key">
                  <strong>La plataforma no tiene FACTURAPI_USER_TEST_KEY en el backend.</strong>{" "}
                  Contacta al administrador para capturarla en <code>/app/backend/.env</code>{" "}
                  antes de iniciar la configuración. Sin esta variable no se pueden crear
                  organizaciones en Facturapi.
                </div>
              )}

              <div className="pt-1">
                <Button
                  data-testid="cfdi-setupcard-iniciar"
                  onClick={() => setOpenWizard(true)}
                  disabled={!estado?.plataforma_user_key_configurada}
                >
                  <Settings2 className="h-4 w-4 mr-2" />
                  Iniciar configuración
                </Button>
              </div>
            </div>
          </div>
          {/* WizardConfig sin trigger (controlado): el botón único de arriba lo abre. */}
          <WizardConfig
            estado={estado} onReload={reload}
            hideTrigger={true}
            openControlled={openWizard}
            onOpenChange={setOpenWizard}
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger data-testid="cfdi-tab-timbrables" value="timbrables">
            Pagos timbrables ({timbrables.length})
          </TabsTrigger>
          <TabsTrigger data-testid="cfdi-tab-desembolsos" value="desembolsos">
            Desembolsos por facturar ({desembolsosPendientes.length})
          </TabsTrigger>
          <TabsTrigger data-testid="cfdi-tab-creditos-ppd" value="creditos_ppd">
            Créditos sin factura PPD ({creditosPPD.length})
          </TabsTrigger>
          <TabsTrigger data-testid="cfdi-tab-emitidos" value="emitidos">
            CFDI emitidos ({contadores.vigente || 0})
          </TabsTrigger>
          <TabsTrigger data-testid="cfdi-tab-cancelados" value="cancelados">
            Cancelados ({contadores.cancelado || 0})
          </TabsTrigger>
          <TabsTrigger data-testid="cfdi-tab-errores" value="errores">
            Errores ({contadores.error || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timbrables" className="mt-4">
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-2">Recibo</th>
                  <th className="p-2">Fecha</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Cliente / Integrante</th>
                  <th className="p-2">RFC</th>
                  <th className="p-2">Crédito</th>
                  <th className="p-2 text-right">Interés</th>
                  <th className="p-2 text-right">Moratorio</th>
                  <th className="p-2 text-right">GC</th>
                  <th className="p-2 text-center">Esquema</th>
                  <th className="p-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="cfdi-tabla-timbrables">
                {timbrables.length === 0 && (
                  <tr><td colSpan="11" className="p-4 text-center text-slate-500">
                    No hay pagos pendientes de timbrado.
                  </td></tr>
                )}
                {/* Bloque 3 (feb-2026) — En grupal, un pago se expande en N filas
                    (una por integrante) con `row_key = pago_id::cliente_id`. Cada fila
                    trae RFC del integrante y su porción. Timbrar A no bloquea B/C. */}
                {timbrables.map(p => {
                  const rowKey = p.row_key || (p.integrante_cliente_id
                    ? `${p.id}::${p.integrante_cliente_id}` : p.id);
                  const btnKey = p.integrante_cliente_id
                    ? `${p.id}-${p.integrante_cliente_id}` : p.id;
                  return (
                    <tr key={rowKey} className="border-t hover:bg-slate-50"
                        data-testid={`cfdi-row-timbrable-${btnKey}`}>
                      <td className="p-2 font-mono text-xs">{p.recibo}</td>
                      <td className="p-2">{fdate(p.fecha_pago)}</td>
                      <td className="p-2">
                        {p.concepto_arrendamiento ? (
                          <BadgeArrendamiento tipo={p.concepto_arrendamiento}
                                              testid={`cfdi-tipo-arr-pago-${btnKey}`} />
                        ) : p.es_grupal ? (
                          <Badge variant="outline"
                                 className="bg-indigo-100 text-indigo-800 border-indigo-300"
                                 data-testid={`cfdi-tipo-grupal-pago-${btnKey}`}>
                            Grupal · {p.integrante_rol === "titular" ? "Titular" : "Obligado"}
                          </Badge>
                        ) : (
                          <Badge variant="outline"
                                 className="bg-slate-100 text-slate-700 border-slate-300"
                                 data-testid={`cfdi-tipo-individual-pago-${p.id}`}>
                            Individual
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">{p.es_grupal ? (p.integrante_nombre || "—") : p.cliente}</td>
                      <td className="p-2 font-mono text-xs">{p.rfc || "—"}</td>
                      <td className="p-2 font-mono text-xs">{p.numero_credito}</td>
                      <td className="p-2 text-right">{mxn(p.aplicacion_interes)}</td>
                      <td className="p-2 text-right">{mxn(p.aplicacion_moratorio)}</td>
                      <td className="p-2 text-right">{mxn(p.aplicacion_gastos_cobranza)}</td>
                      <td className="p-2 text-center">
                        {p.necesita === "complemento" ? (
                          <Badge variant="outline" className="bg-sky-100 text-sky-800 border-sky-300"
                                 data-testid={`cfdi-esquema-complemento-${btnKey}`}>
                            Complemento PPD
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300"
                                 data-testid={`cfdi-esquema-pue-${btnKey}`}>
                            CFDI PUE
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 text-center">
                        {canEdit && (
                          <Button data-testid={`cfdi-btn-timbrar-${btnKey}`} size="sm"
                                  variant="outline" onClick={() => timbrarPago(p)}>
                            {p.necesita === "complemento"
                              ? <FileStack className="h-4 w-4 mr-1" />
                              : <Sparkles className="h-4 w-4 mr-1" />}
                            {p.necesita === "complemento" ? "Complementar" : "Timbrar"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="desembolsos" className="mt-4">
          {/* Gap 1 corregido (feb-2026 iter_07-28h) — lista de créditos con
              comisión de apertura descontada pendiente de facturar. Botón reutiliza
              PreviewTimbrado con tipo="desembolso" y la conciliación dura contra
              el crédito.
              FASE B (feb-2026): un crédito grupal expande N filas (una por integrante).
              Cada fila trae `integrante_cliente_id`, `integrante_rol`, `es_grupal`
              y la porción de comisión/IVA/total del miembro. */}
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-2">Crédito</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Cliente / Integrante</th>
                  <th className="p-2">RFC</th>
                  <th className="p-2">Desembolso</th>
                  <th className="p-2">Cobro</th>
                  <th className="p-2 text-right">Comisión</th>
                  <th className="p-2 text-right">IVA</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="cfdi-tabla-desembolsos-pendientes">
                {desembolsosPendientes.length === 0 && (
                  <tr><td colSpan="10" className="p-4 text-center text-slate-500">
                    No hay comisiones de apertura pendientes de facturar.
                  </td></tr>
                )}
                {desembolsosPendientes.map(r => {
                  // Key único para poder expandir N filas por crédito grupal.
                  const rowKey = r.es_grupal
                    ? `${r.credito_id}::${r.integrante_cliente_id}`
                    : r.credito_id;
                  const btnTestId = r.es_grupal
                    ? `cfdi-btn-emitir-desembolso-${r.credito_id}-${r.integrante_cliente_id}`
                    : `cfdi-btn-emitir-desembolso-${r.credito_id}`;
                  const rowTestId = r.es_grupal
                    ? `cfdi-row-desembolso-${r.credito_id}-${r.integrante_cliente_id}`
                    : `cfdi-row-desembolso-${r.credito_id}`;
                  return (
                    <tr key={rowKey} className="border-t hover:bg-slate-50"
                        data-testid={rowTestId}>
                      <td className="p-2 font-mono text-xs">{r.numero_credito}</td>
                      <td className="p-2 text-xs">
                        {r.es_grupal ? (
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-300"
                                 data-testid={`cfdi-tipo-grupal-${r.credito_id}-${r.integrante_cliente_id}`}>
                            Grupal · {r.integrante_rol || "integ."}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">
                            Individual
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">{r.cliente_nombre || "—"}</td>
                      <td className="p-2 font-mono text-xs">{r.cliente_rfc || "—"}</td>
                      <td className="p-2 text-xs">
                        {r.fecha_desembolso ? String(r.fecha_desembolso).slice(0, 10) : "—"}
                      </td>
                      <td className="p-2 text-xs">
                        {r.comision_cobro_modo === "descontada"
                           ? "Descontada"
                           : r.comision_cobro_modo || "—"}
                      </td>
                      <td className="p-2 text-right font-mono">{mxn(r.comision_base)}</td>
                      <td className="p-2 text-right font-mono">{mxn(r.iva_comision)}</td>
                      <td className="p-2 text-right font-mono font-semibold">
                        {mxn(r.total_comision)}
                      </td>
                      <td className="p-2 text-center">
                        {canEdit && (
                          <Button data-testid={btnTestId}
                                  size="sm" variant="outline"
                                  onClick={() => emitirComisionDesembolso(r)}>
                            <FileText className="h-4 w-4 mr-1" />
                            Timbrar comisión
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="creditos_ppd" className="mt-4">
          <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
            <FileStack className="h-4 w-4 inline mr-1" />
            <strong>Créditos a plazos sin factura de ingreso PPD.</strong> Antes de complementar los
            pagos de estos créditos, emite primero la factura de ingreso PPD (padre) que
            representa los intereses totales del plan. Cada pago del acreditado emitirá un
            Complemento de Pago que reduce el saldo pendiente.
          </div>
          <div className="rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-2">Crédito</th>
                  <th className="p-2">Cliente</th>
                  <th className="p-2">Plazo</th>
                  <th className="p-2">Periodicidad</th>
                  <th className="p-2">Régimen IVA</th>
                  <th className="p-2 text-right">Interés total</th>
                  <th className="p-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="cfdi-tabla-creditos-ppd">
                {creditosPPD.length === 0 && (
                  <tr><td colSpan="7" className="p-4 text-center text-slate-500">
                    No hay créditos pendientes de factura PPD.
                  </td></tr>
                )}
                {creditosPPD.map(c => (
                  <tr key={c.credito_id} className="border-t hover:bg-slate-50">
                    <td className="p-2 font-mono text-xs">{c.numero_credito}</td>
                    <td className="p-2">{c.cliente_nombre || "—"}</td>
                    <td className="p-2">{c.plazo}</td>
                    <td className="p-2">{c.periodicidad || "—"}</td>
                    <td className="p-2">{c.iva_intereses_credito || "—"}</td>
                    <td className="p-2 text-right">{mxn(c.interes_total)}</td>
                    <td className="p-2 text-center">
                      {canEdit && (
                        <Button data-testid={`cfdi-btn-emitir-ppd-${c.credito_id}`}
                                size="sm" variant="outline"
                                onClick={() => emitirFacturaPPD(c)}>
                          <FileStack className="h-4 w-4 mr-1" />
                          Emitir factura PPD
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="emitidos" className="mt-4">
          <TablaCFDI cfdiList={cfdiList.filter(c => c.estado === "vigente")}
                     onDescargarXml={descargarXml} onDescargarPdf={descargarPdf}
                     onEnviar={enviarCorreo} onCancelar={setCancelCfdi}
                     onSustituir={setSustituirCfdi}
                     canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="cancelados" className="mt-4">
          <TablaCFDI cfdiList={cfdiList.filter(c => ["cancelado", "saldada"].includes(c.estado))}
                     onDescargarXml={descargarXml} onDescargarPdf={descargarPdf}
                     onDescargarAcuseXml={descargarAcuseXml}
                     onDescargarAcusePdf={descargarAcusePdf}
                     onReintentarAcuse={reintentarAcuse}
                     canEdit={false} mostrarAcuse />
        </TabsContent>
        <TabsContent value="errores" className="mt-4">
          <TablaErrores cfdiList={cfdiList.filter(c => c.estado === "error")} />
        </TabsContent>
      </Tabs>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview CFDI · Timbrado</DialogTitle>
            <DialogDescription>
              Revisa el desglose antes de enviar al PAC. Cancelar cerrará sin timbrar.
            </DialogDescription>
          </DialogHeader>
          {showPreview && (
            <PreviewTimbrado
              pagoId={selectedPago} credId={selectedCred} tipo={previewTipo}
              integranteClienteId={selectedIntegrante}
              estado={estado}
              onEmitido={() => { setShowPreview(false); reload(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {cancelCfdi && (
        <DialogCancelar cfdi={cancelCfdi} open={!!cancelCfdi}
                        onOpenChange={o => !o && setCancelCfdi(null)}
                        onCancelado={reload} />
      )}

      {sustituirCfdi && (
        <Dialog open={!!sustituirCfdi} onOpenChange={o => !o && setSustituirCfdi(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Sustituir CFDI (motivo 01)</DialogTitle>
              <DialogDescription>
                Se emitirá un CFDI nuevo con relación 04 al UUID actual{" "}
                <code>{sustituirCfdi.uuid_sat?.slice(0, 20)}…</code> y luego se cancelará el
                original. Todo en una operación.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>La sustitución del CFDI requiere aceptación del receptor</strong> si ya
              transcurrieron más de 24 horas o si el receptor no está en el régimen simplificado
              de confianza. Confirma con tu contador antes de proceder.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSustituirCfdi(null)}>Cancelar</Button>
              <Button data-testid="cfdi-sustituir-confirmar" onClick={sustituir}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Sustituir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Build 4 · Arrendamiento: el CFDI de la renta (capital + interés) y el de la opción de compra.
function BadgeArrendamiento({ tipo, testid }) {
  const enajenacion = tipo === "enajenacion";
  return (
    <Badge variant="outline" data-testid={testid}
           className={enajenacion ? "bg-cyan-100 text-cyan-800 border-cyan-300"
                                  : "bg-amber-100 text-amber-800 border-amber-300"}>
      {enajenacion ? "Enajenación de bien" : "Renta"}
    </Badge>
  );
}

function TablaCFDI({ cfdiList, onDescargarXml, onDescargarPdf, onEnviar, onCancelar,
                    onSustituir, canEdit,
                    onDescargarAcuseXml, onDescargarAcusePdf, onReintentarAcuse,
                    mostrarAcuse = false }) {
  if (cfdiList.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-500">Sin CFDI en esta categoría.</div>;
  }
  return (
    <div className="rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left">
          <tr>
            <th className="p-2">UUID</th>
            <th className="p-2">Tipo</th>
            <th className="p-2">Fecha</th>
            <th className="p-2">Origen</th>
            <th className="p-2">RFC receptor</th>
            <th className="p-2 text-right">Total</th>
            <th className="p-2 text-right">Saldo PPD</th>
            <th className="p-2 text-center">Estado</th>
            {mostrarAcuse && <th className="p-2 text-center">Acuse SAT</th>}
            <th className="p-2 text-center">Acciones</th>
          </tr>
        </thead>
        <tbody data-testid="cfdi-tabla-emitidos">
          {cfdiList.map(c => (
            <tr key={c.id} className="border-t hover:bg-slate-50">
              <td className="p-2 font-mono text-[10px]">{c.uuid_sat || "—"}</td>
              <td className="p-2">
                {c.concepto_arrendamiento ? (
                  <BadgeArrendamiento tipo={c.concepto_arrendamiento} testid={`cfdi-tipo-arr-${c.id}`} />
                ) : (
                <Badge variant="outline" className={c.tipo === "P"
                    ? "bg-sky-100 text-sky-800"
                    : c.tipo_origen === "factura_ppd"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-emerald-100 text-emerald-800"}>
                  {c.tipo === "P" ? "Complemento" :
                   c.tipo_origen === "factura_ppd" ? "Factura PPD" :
                   c.tipo_origen === "desembolso" ? "Desembolso" : "Ingreso PUE"}
                </Badge>
                )}
              </td>
              <td className="p-2">{fdate(c.fecha_emision || c.created_at)}</td>
              <td className="p-2 text-xs">{c.tipo_origen === "pago" ? "Pago" :
                    c.tipo_origen === "desembolso" ? "Desembolso" :
                    c.tipo_origen === "factura_ppd" ? "Factura PPD" :
                    c.tipo_origen === "complemento" ? "Complemento" : c.tipo_origen}</td>
              <td className="p-2 font-mono">{c.receptor_rfc || "—"}</td>
              <td className="p-2 text-right">{mxn(c.total || c.monto_pago)}</td>
              <td className="p-2 text-right">
                {c.tipo_origen === "factura_ppd"
                  ? <span className={c.saldo_pendiente <= 0 ? "text-emerald-700" : ""}>{mxn(c.saldo_pendiente ?? 0)}</span>
                  : "—"}
              </td>
              <td className="p-2 text-center"><ChipEstado estado={c.estado} /></td>
              {mostrarAcuse && (
                <td className="p-2 text-center">
                  {c.estado === "cancelado" ? (
                    c.acuse_xml_key && c.acuse_pdf_key ? (
                      <div className="flex justify-center gap-1"
                           data-testid={`cfdi-acuse-listo-${c.id}`}>
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                                data-testid={`cfdi-acuse-xml-${c.id}`}
                                onClick={() => onDescargarAcuseXml && onDescargarAcuseXml(c.id)}
                                title="Descargar acuse de cancelación (XML)">
                          <FileCheck2 className="h-3.5 w-3.5 mr-1" />
                          XML
                        </Button>
                        <Button size="sm" variant="outline"
                                className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                                data-testid={`cfdi-acuse-pdf-${c.id}`}
                                onClick={() => onDescargarAcusePdf && onDescargarAcusePdf(c.id)}
                                title="Descargar acuse de cancelación (PDF)">
                          <Download className="h-3.5 w-3.5 mr-1" />
                          PDF
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1"
                           data-testid={`cfdi-acuse-pendiente-${c.id}`}>
                        <Badge variant="outline"
                               className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]">
                          <Clock className="h-3 w-3 mr-1" />
                          Acuse pendiente
                        </Badge>
                        <Button size="sm" variant="ghost"
                                className="h-6 px-2 text-[10px] text-amber-800 hover:bg-amber-100"
                                data-testid={`cfdi-acuse-retry-${c.id}`}
                                onClick={() => onReintentarAcuse && onReintentarAcuse(c.id)}
                                title="Reintentar descarga del acuse SAT">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Obtener acuse
                        </Button>
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              )}
              <td className="p-2 text-center">
                <div className="flex justify-center gap-1">
                  <Button size="sm" variant="ghost"
                          data-testid={`cfdi-descargar-xml-${c.id}`}
                          onClick={() => onDescargarXml(c.id)}>
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost"
                          data-testid={`cfdi-descargar-pdf-${c.id}`}
                          onClick={() => onDescargarPdf(c.id)}>
                    <Download className="h-4 w-4" />
                  </Button>
                  {canEdit && onEnviar && (
                    <Button size="sm" variant="ghost"
                            data-testid={`cfdi-enviar-${c.id}`}
                            onClick={() => onEnviar(c.id)}>
                      <Mail className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && onSustituir && c.tipo === "I" && c.estado === "vigente" && (
                    <Button size="sm" variant="ghost" className="text-sky-700"
                            data-testid={`cfdi-sustituir-btn-${c.id}`}
                            onClick={() => onSustituir(c)}
                            title="Sustituir con relación 04 (motivo 01)">
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && onCancelar && c.estado === "vigente" && (
                    <Button size="sm" variant="ghost" className="text-red-600"
                            data-testid={`cfdi-cancelar-btn-${c.id}`}
                            onClick={() => onCancelar(c)}>
                      <XCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TablaErrores({ cfdiList }) {
  if (cfdiList.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-500">Sin errores registrados.</div>;
  }
  return (
    <div className="rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left">
          <tr>
            <th className="p-2">Fecha</th>
            <th className="p-2">Origen</th>
            <th className="p-2">Mensaje del PAC</th>
          </tr>
        </thead>
        <tbody data-testid="cfdi-tabla-errores">
          {cfdiList.map(c => (
            <tr key={c.id} className="border-t">
              <td className="p-2">{fdate(c.created_at)}</td>
              <td className="p-2">{c.tipo_origen}</td>
              <td className="p-2 text-red-700">{c.mensaje_error || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
